import React from 'react';
import { motion } from 'framer-motion';

export default function RecommendationCard({ title, data, index = 0 }) {
    if (!data) return null;

    const urgency = data.urgency || data.tingkatUrgency || 'low';
    const tindakLanjut = data.followUp || data.tindakLanjut;
    const isCritical = urgency === 'high';

    const getStatusTheme = (status) => {
        if (!status) return { text: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200', icon: '📝' };
        
        if (status.includes('Sesuai') || status.includes('Normal') || status.includes('Rendah')) {
            return { text: 'text-emerald-700', bg: 'bg-emerald-50/80', border: 'border-emerald-200', icon: '✨' };
        } else if (status.includes('Meragukan') || status.includes('Sedang') || status.includes('Ringan')) {
            return { text: 'text-amber-700', bg: 'bg-amber-50/80', border: 'border-amber-200', icon: '⚠️' };
        } else if (status.includes('Penyimpangan') || status.includes('Tinggi') || status.includes('GPPH') || status.includes('Gangguan') || status.includes('Terdeteksi')) {
            return { text: 'text-rose-700', bg: 'bg-rose-50/80', border: 'border-rose-200', icon: '🚨' };
        }
        return { text: 'text-gray-700', bg: 'bg-gray-50/80', border: 'border-gray-200', icon: '📋' };
    };

    const theme = getStatusTheme(data.status);

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: index * 0.1 } }
    };

    if (isCritical) {
        return (
            <motion.div 
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                className="mb-6 relative group overflow-hidden rounded-[2rem] p-6 bg-gradient-to-br from-rose-500 via-red-500 to-rose-600 border border-rose-400 shadow-[0_15px_40px_rgba(225,29,72,0.25)] hover:shadow-[0_15px_50px_rgba(225,29,72,0.35)] transition-shadow duration-500"
            >
                {/* Decorative blob for critical card */}
                <div className="absolute -top-16 -right-16 w-48 h-48 bg-white rounded-full mix-blend-overlay filter blur-[40px] opacity-30 pointer-events-none group-hover:opacity-50 transition-opacity duration-700"></div>
                <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-black rounded-full mix-blend-overlay filter blur-[40px] opacity-20 pointer-events-none"></div>
                
                <div className="relative z-10">
                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-[1rem] flex items-center justify-center text-2xl shadow-inner border border-white/40">
                                🚨
                            </div>
                            <div>
                                <h3 className="text-xl font-extrabold text-white tracking-tight leading-tight">{title}</h3>
                                <span className="inline-block px-3 py-1 mt-1.5 rounded-full text-[10px] font-extrabold tracking-widest uppercase bg-white/90 text-rose-600 shadow-sm backdrop-blur-md">
                                    Tindakan Segera
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white/10 backdrop-blur-md border border-white/30 p-5 rounded-2xl mb-5 shadow-inner">
                        <div className="font-extrabold text-white text-xl mb-1.5">{data.status}</div>
                        {data.detail && <div className="text-sm text-rose-100 font-medium">{data.detail}</div>}
                        <div className="w-8 h-1 bg-white/30 rounded-full my-3"></div>
                        <p className="text-white text-[15px] leading-relaxed font-medium opacity-95">
                            {data.interpretasi}
                        </p>
                    </div>

                    {tindakLanjut && (
                        <div className="bg-white/95 backdrop-blur-xl p-5 rounded-2xl mb-4 shadow-lg border border-white/50">
                            <h4 className="font-extrabold text-rose-600 mb-2 flex items-center text-xs uppercase tracking-widest">
                                <span className="mr-2 text-lg">⚡</span> Instruksi Rujukan
                            </h4>
                            <p className="text-gray-800 text-[15px] font-bold leading-relaxed">{tindakLanjut}</p>
                        </div>
                    )}

                    {data.rekomendasi && (
                        <div className="bg-rose-900/30 border border-rose-300/30 p-5 rounded-2xl backdrop-blur-md mt-4">
                            <h4 className="font-bold text-rose-100 mb-3 text-xs uppercase tracking-widest flex items-center">
                                <span className="mr-2">💡</span> Saran Tambahan
                            </h4>
                            <ul className="text-white text-sm space-y-2.5">
                                {(Array.isArray(data.rekomendasi) ? data.rekomendasi : [data.rekomendasi]).map((item, idx) => (
                                    <li key={idx} className="flex items-start space-x-3">
                                        <span className="flex-shrink-0 mt-0.5 text-rose-200">✨</span>
                                        <span className="leading-relaxed opacity-95 font-medium">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </motion.div>
        );
    }

    // Normal Card (Low / Medium Urgency)
    return (
        <motion.div 
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            className="mb-6 relative group bg-white/70 backdrop-blur-2xl border border-white/80 rounded-[2rem] p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.08)] hover:bg-white/90 transition-all duration-500 overflow-hidden"
        >
            {/* Subtle glow effect */}
            <div className={`absolute -right-10 -top-10 w-40 h-40 rounded-full mix-blend-multiply filter blur-[40px] opacity-20 pointer-events-none transition-all duration-500 ${theme.bg.replace('/80', '')}`}></div>

            <div className="space-y-5 relative z-10">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-[1rem] ${theme.bg} ${theme.border} border flex items-center justify-center text-lg shadow-sm`}>
                            {theme.icon}
                        </div>
                        <h3 className="text-lg font-extrabold text-gray-800 tracking-tight">{title}</h3>
                    </div>
                    {urgency === 'medium' && (
                        <span className="px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase border bg-amber-100 border-amber-300 text-amber-800 shadow-sm">
                            Perhatian
                        </span>
                    )}
                </div>

                {/* Status Section */}
                <div className={`p-5 rounded-[1.5rem] border backdrop-blur-md transition-colors ${theme.bg} ${theme.border}`}>
                    <div className={`font-extrabold text-lg mb-1 ${theme.text}`}>{data.status}</div>
                    {data.detail && <div className={`text-sm font-medium opacity-90 ${theme.text}`}>{data.detail}</div>}
                    <div className={`w-8 h-1 rounded-full my-3 opacity-30 bg-current ${theme.text}`}></div>
                    <p className={`text-[14px] leading-relaxed font-medium opacity-90 ${theme.text}`}>
                        {data.interpretasi}
                    </p>
                </div>

                {/* Tindak Lanjut */}
                {tindakLanjut && (
                    <div className="bg-gradient-to-br from-indigo-50 to-sky-50 border border-indigo-100/60 p-5 rounded-[1.5rem] shadow-sm">
                        <h4 className="font-extrabold text-indigo-800 mb-2 text-xs uppercase tracking-widest flex items-center">
                            <span className="mr-2">🎯</span> Tindak Lanjut
                        </h4>
                        <p className="text-indigo-900 text-sm font-semibold leading-relaxed">{tindakLanjut}</p>
                    </div>
                )}

                {/* Rekomendasi */}
                {data.rekomendasi && (
                    <div className="bg-gray-50/80 backdrop-blur-sm border border-gray-100 p-5 rounded-[1.5rem]">
                        <h4 className="font-extrabold text-gray-700 mb-3 text-xs uppercase tracking-widest flex items-center">
                            <span className="mr-2 text-gray-400">📝</span> Rekomendasi
                        </h4>
                        <ul className="space-y-2.5">
                            {(Array.isArray(data.rekomendasi) ? data.rekomendasi : [data.rekomendasi]).map((item, idx) => (
                                <li key={idx} className="flex items-start space-x-3 text-gray-700">
                                    <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-[8px] font-bold text-gray-600">
                                        {idx + 1}
                                    </span>
                                    <span className="text-sm font-medium leading-relaxed">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
